import { createContext, useContext, useState, useCallback, useEffect } from "react";

const BetSlipContext = createContext(null);
const KEY = "pnrbet_slip_v1";

// Legs are identified by a composite key because outcome ids are only unique
// WITHIN an event (the server assigns "0","1",... per event).
const sameLeg = (a, b) => a.eventId === b.eventId && String(a.outcomeId) === String(b.outcomeId);
const isGameLine = (leg) => (leg.market || "") !== "prop";

export function BetSlipProvider({ children }) {
  const [legs, setLegs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  const [freeBet, setFreeBet] = useState(null); // selected free-bet token id (or null)
  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify(legs)); } catch { /* ignore */ }
  }, [legs]);

  const has = useCallback((eventId, outcomeId) =>
    legs.some((l) => l.eventId === eventId && String(l.outcomeId) === String(outcomeId)), [legs]);

  const toggle = useCallback((leg) => {
    setLegs((prev) => {
      if (prev.some((l) => sameLeg(l, leg))) return prev.filter((l) => !sameLeg(l, leg));
      let base = prev;
      if (isGameLine(leg)) {
        // One game line (moneyline/spread/total) per match — a new one replaces the old.
        base = base.filter((l) => !(l.eventId === leg.eventId && isGameLine(l)));
      } else if (leg.propKey) {
        // One side per prop market (bug #5): adding Over removes a same-market Under.
        base = base.filter((l) => !(l.eventId === leg.eventId && l.propKey && l.propKey === leg.propKey));
      }
      return [...base, leg];
    });
  }, []);

  const remove = useCallback((eventId, outcomeId) =>
    setLegs((p) => p.filter((l) => !(l.eventId === eventId && String(l.outcomeId) === String(outcomeId)))), []);

  const clear = useCallback(() => { setLegs([]); setFreeBet(null); }, []);

  // updates keyed by `${eventId}:${outcomeId}`; accepts a number (legacy odds-only)
  // or { odds, marketVersion, receivedAt, providerUpdatedAt, expiresAt }.
  const setOdds = useCallback((updates) => {
    setLegs((prev) => prev.map((l) => {
      const k = `${l.eventId}:${l.outcomeId}`;
      const update = updates[k];
      if (update == null) return l;
      if (typeof update !== "object") return { ...l, odds: update };
      return {
        ...l,
        ...(update.odds != null ? { odds: update.odds } : {}),
        ...(update.marketVersion != null ? { marketVersion: update.marketVersion } : {}),
        ...("line" in update ? { line: update.line } : {}),
        ...(update.label != null ? { label: update.label } : {}),
        ...(update.side != null ? { side: update.side } : {}),
        ...(update.market != null ? { market: update.market } : {}),
        ...(update.receivedAt != null ? { receivedAt: update.receivedAt } : {}),
        ...(update.providerUpdatedAt != null ? { providerUpdatedAt: update.providerUpdatedAt } : {}),
        ...(update.expiresAt != null ? { expiresAt: update.expiresAt } : {}),
      };
    }));
  }, []);

  return (
    <BetSlipContext.Provider value={{ legs, has, toggle, remove, clear, setOdds, freeBet, setFreeBet, count: legs.length }}>
      {children}
    </BetSlipContext.Provider>
  );
}

export const useBetSlip = () => useContext(BetSlipContext);
